import { Button, NotificationBanner } from '@velocity/ui';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getDocument, submitTrigger } from '../../../../api/instant-lease-api';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import sequenceConfig from '../../../../sequence/sequence-config';
import { Document } from '../../../../types/instant-lease-api';
import {
  getChildrenByNumber,
  getCoApplicant,
  getEmploymentByType,
  getMainApplicant,
  getMaritalStatusByType,
  getResidenceByType,
  getText,
} from '../../../../utils/getter';
import { mapAddressField } from '../../../../utils/helper';
import { errorLogging } from '../../../../utils/logging';
import Navigation from '../../../../utils/navigation';
import { formatNumberWithThousandsSeparator } from '../../../../utils/number-format';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import ConfirmSection from '../../../b2b/molecules/confirm-section/confirm-section';
import { ibanFormat } from '../../../common/input-field/iban-field';
import './data-summary.css';

const DataSummary: React.FC = () => {
  // Lease Application Storage
  const { activeApplication, selectedDealer } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Auth Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // Dispatch hook
  const dispatch = useAppDispatch();

  // Init history hook (used in nav)
  const navigate = useNavigate();

  // Navigation class
  const { nextRoute, goToStep } = new Navigation();

  // Opt-out of adding a co-applicant
  const [withoutCoApplicant, setWithoutCoApplicant] = useState<boolean>(false);

  // Loading State
  const [loading, setLoading] = useState<boolean>(false);

  // Next page
  const next = async () => {
    setLoading(true);
    if (activeApplication) {
      submitTrigger(
        accessToken,
        activeApplication.uuid,
        'confirm_customer_data',
      )
        .then(() => {
          setLoading(false);
          navigate(nextRoute());
        })
        .catch(() => {
          setLoading(false);
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
          errorLogging(
            new Error(
              `unable to submit data summary to user with application id ${activeApplication?.uuid}`,
            ),
          );
        });
    }
  };

  // Get person
  const privateCustomer = activeApplication?.customer?.private;

  const hasPartner =
    privateCustomer?.signers && privateCustomer?.signers?.length > 1;

  const mainSigner = getMainApplicant(privateCustomer?.signers);

  const partner = getCoApplicant(privateCustomer?.signers);

  const mainSignerDocuments = activeApplication?.registered_documents?.filter(
    (document) => {
      if (
        mainSigner?.person?.documents?.find((token) => token === document.token)
      ) {
        return document;
      }
      return false;
    },
  );

  const coSignerDocuments = activeApplication?.registered_documents?.filter(
    (document) => {
      if (
        partner?.person?.documents?.find((token) => token === document.token)
      ) {
        return document;
      }
      return false;
    },
  );

  // Getting step name by number
  const getStepNumberByName = (key: string): number =>
    sequenceConfig(activeApplication as any)[0].steps.findIndex(
      (step) => step.key === key,
    );
  // Open file in a new tab
  const openFile = (document: Document | undefined) => {
    if (document) {
      getDocument(accessToken, activeApplication?.uuid || '', document.token)
        .then((response) => {
          const blob = window.URL.createObjectURL(
            new Blob([response], { type: document.mime_type }),
          );
          const link = window.document.createElement('a');
          link.href = blob;
          window.open(link.href, '_blank', 'noopener,noreferrer');
        })
        .catch(() => {
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
        });
    }
  };

  const signerIdentityDocument = () => {
    const docID =
      mainSigner?.person?.identity_documents &&
      mainSigner?.person?.identity_documents?.length > 0 &&
      mainSigner?.person?.identity_documents[0]?.document_id;

    return activeApplication?.registered_documents?.find(
      (doc) => doc.token === docID,
    );
  };

  const partnerIdentityDocument = () => {
    const docID =
      partner?.person?.identity_documents &&
      partner?.person?.identity_documents?.length > 0 &&
      partner?.person?.identity_documents[0]?.document_id;

    return activeApplication?.registered_documents?.find(
      (doc) => doc.token === docID,
    );
  };

  const ellipsisFileName = (fileName: string): string => {
    if (fileName.length > 35) {
      return (
        fileName.substr(0, 10) +
        '...' +
        fileName.substr(fileName.length - 10, fileName.length)
      );
    }
    return fileName;
  };

  const sumUnits = (...values: (string | undefined)[]): number =>
    values.reduce((total, value) => total + parseInt(value || '0'), 0);

  const incomeDetails = privateCustomer?.income_and_cost_details;
  const totalIncome = sumUnits(
    incomeDetails?.income?.units,
    incomeDetails?.additional_income?.units,
  );
  const totalExpenses = sumUnits(
    incomeDetails?.housing_cost?.units,
    incomeDetails?.additional_cost?.units,
  );
  const formatAmount = (amount: number, suffix: string) =>
    `${formatNumberWithThousandsSeparator(`${amount}`)} ${suffix}`;

  const incomeDocuments = (
    mainSignerDocuments && mainSignerDocuments.length > 0
      ? mainSignerDocuments
      : activeApplication?.registered_documents
  )?.filter((document) => document.document_type === 'PROOF_OF_INCOME');

  const identityDocument = signerIdentityDocument();

  const canContinue = hasPartner || withoutCoApplicant;

  return (
    <Layout
      heading='Zusammenfassung deiner Angaben'
      subHeading='Bitte prüfe deine Angaben sorgfältig, bevor du fortfährst.'
      stepStatus
      className='summary-page'
    >
      <h2 className='summary-group-heading' data-testid='main-signer-heading'>
        Hauptantragsteller
      </h2>

      <ConfirmSection
        card
        sectionTitle='Deine Persönlichen Daten'
        step={getStepNumberByName('personalInformation')}
        elements={[
          {
            title: 'Name',
            value: `${privateCustomer?.person?.first_name} ${privateCustomer?.person?.last_name}`,
          },
          {
            title: 'Meldeadresse',
            value: (
              <>
                {mapAddressField(
                  privateCustomer?.person?.contact_details,
                  'street_line',
                )}{' '}
                {mapAddressField(
                  privateCustomer?.person?.contact_details,
                  'house_number',
                )}
                <br />
                {mapAddressField(
                  privateCustomer?.person?.contact_details,
                  'postal_code',
                )}{' '}
                {mapAddressField(
                  privateCustomer?.person?.contact_details,
                  'locality',
                )}
                <br />
                {mapAddressField(
                  privateCustomer?.person?.contact_details,
                  'additional_lines',
                )}
              </>
            ),
          },
          {
            title: 'Geburtsdaten',
            value: (
              <>
                {privateCustomer?.person?.date_of_birth} in{' '}
                {privateCustomer?.person?.birth_place}
              </>
            ),
          },
        ]}
      />

      {selectedDealer && (
        <ConfirmSection
          card
          sectionTitle='Dein Händler'
          step={getStepNumberByName('dealerSelection')}
          elements={[
            { title: 'Firma', value: selectedDealer.name1 },
            {
              title: 'Firmenadresse',
              value: (
                <>
                  {selectedDealer.street}
                  <br />
                  {selectedDealer.zip_code} {selectedDealer.city}
                </>
              ),
            },
          ]}
        />
      )}

      <ConfirmSection
        card
        sectionTitle='Kontakt'
        step={getStepNumberByName('contact')}
        elements={[
          {
            title: 'Mobilnummer',
            value: (
              <>
                {privateCustomer?.person?.contact_details?.phone_numbers
                  ? privateCustomer?.person?.contact_details?.phone_numbers[0]
                      .value
                  : ''}
              </>
            ),
          },
          {
            title: 'E-Mail Adresse',
            value: (
              <>
                {privateCustomer?.person?.contact_details?.emails
                  ? privateCustomer?.person?.contact_details?.emails[0].value
                  : ''}
              </>
            ),
          },
        ]}
      />

      <ConfirmSection
        card
        sectionTitle='Beruf, Familie & Wohnen'
        step={getStepNumberByName('workFamilyHousing')}
        elements={[
          {
            title: 'Beschäftigung',
            value: (
              <>
                {
                  getEmploymentByType(
                    privateCustomer?.living_details?.employment_type || '',
                  )?.label
                }{' '}
                | seit {privateCustomer?.living_details?.employment_since}
              </>
            ),
          },
          {
            title: 'Familienstand',
            value: (
              <>
                {
                  getMaritalStatusByType(
                    privateCustomer?.living_details?.marital_status || '',
                  )?.label
                }{' '}
                |{' '}
                {
                  getChildrenByNumber(
                    privateCustomer?.living_details?.number_of_children,
                  )?.value
                }{' '}
                unterhaltspflichtige Kinder
              </>
            ),
          },
          {
            title: 'Wohnsituation',
            value: (
              <>
                {
                  getResidenceByType(
                    privateCustomer?.living_details?.residence_type,
                  )?.label
                }
                <br />
                seit {privateCustomer?.living_details?.residence_since}
              </>
            ),
          },
        ]}
      />

      <ConfirmSection
        card
        sectionTitle='Budget'
        step={getStepNumberByName('incomeAndExpenses')}
        elements={[
          {
            title: 'Gesamte Einnahmen',
            value: formatAmount(totalIncome, '€ / Monat'),
          },
          {
            title: 'Gesamte Ausgaben',
            value: formatAmount(totalExpenses, '€ / Monat'),
          },
          {
            title: 'Budget',
            value: (
              <span
                style={{
                  color: 'var(--vel-color-semantic-warning-high, #d77400)',
                }}
              >
                {formatAmount(totalIncome - totalExpenses, '€ / Monat')}
              </span>
            ),
          },
        ]}
      >
        {!hasPartner && (
          <div className='summary-co-applicant'>
            <NotificationBanner
              kind='information'
              title='Mitantragsteller hinzufügen'
            >
              Ein Mitantragsteller kann deine Bonität verbessern und deine
              Chancen auf eine Zusage erhöhen.
            </NotificationBanner>
            <div className='summary-co-applicant-actions'>
              <Button
                variant='dark-outlined'
                size='m'
                type='button'
                data-testid='add-co-applicant'
                onClick={() =>
                  navigate(
                    goToStep(
                      getStepNumberByName('hasCoApplicant'),
                      0,
                      0,
                      activeApplication as any,
                    ),
                  )
                }
              >
                Mitantragsteller hinzufügen
              </Button>
              <label className='text-tight-200 summary-co-applicant-checkbox'>
                <input
                  type='checkbox'
                  data-testid='without-co-applicant'
                  checked={withoutCoApplicant}
                  onChange={(e) => setWithoutCoApplicant(e.target.checked)}
                />
                Ohne Mitantragsteller fortfahren
              </label>
            </div>
            <p className='text-tight-300 summary-co-applicant-note'>
              <b>Bitte beachte:</b> Wenn deine Bonität allein nicht ausreicht,
              kann dein Antrag ohne Mitantragsteller nicht fortgeführt werden.
            </p>
          </div>
        )}
      </ConfirmSection>

      <ConfirmSection
        card
        sectionTitle='Bankverbindung'
        step={getStepNumberByName('bank')}
        elements={[
          {
            title: 'IBAN',
            value: `${
              privateCustomer?.bank_account?.iban
                ? ibanFormat(privateCustomer?.bank_account?.iban)
                : ''
            }`,
          },
          {
            title: 'Bankinstitut',
            value: `${privateCustomer?.bank_account?.bank_name}`,
          },
        ]}
      />

      <ConfirmSection
        card
        sectionTitle='Dokumentenupload'
        step={getStepNumberByName('uploadDocuments')}
        elements={[
          {
            title: 'Einkommensnachweis',
            condition: !!incomeDocuments?.length,
            value: (
              <>
                {incomeDocuments?.map((document, index) => (
                  <span
                    key={document.token}
                    role='presentation'
                    className='link'
                    style={{ display: 'block' }}
                    onClick={() => openFile(document)}
                  >
                    {index + 1}. {ellipsisFileName(document.file_name)}
                  </span>
                ))}
              </>
            ),
          },
          {
            title: 'Ausweisdokument',
            condition: !!identityDocument,
            value: 'Personalausweis',
            onClick: () => openFile(identityDocument),
          },
        ]}
      />

      {hasPartner && (
        <div
          className='summary-co-applicant-group'
          data-testid='co-applicant-group'
        >
          <h2 className='summary-group-heading'>
            Mitantragsteller: {partner?.person?.first_name}{' '}
            {partner?.person?.last_name}
          </h2>
          <ConfirmSection
            card
            sectionTitle='Persönliche Angaben'
            step={getStepNumberByName('hasCoApplicant')}
            elements={[
              {
                title: 'Namen',
                value: `${partner?.person?.first_name} ${partner?.person?.last_name}`,
                titleClassName: 'small-text title-style',
                valueClassName: 'small-text',
              },
              {
                title: 'Meldeadresse',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: (
                  <>
                    {mapAddressField(
                      partner?.person?.contact_details,
                      'street_line',
                    )}{' '}
                    {mapAddressField(
                      partner?.person?.contact_details,
                      'house_number',
                    )}
                    <br />
                    {mapAddressField(
                      partner?.person?.contact_details,
                      'postal_code',
                    )}{' '}
                    {mapAddressField(
                      partner?.person?.contact_details,
                      'locality',
                    )}
                    <br />
                    {mapAddressField(
                      partner?.person?.contact_details,
                      'additional_lines',
                    )}
                  </>
                ),
              },
              {
                title: 'Geburtsdaten',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: (
                  <>
                    {partner?.person?.date_of_birth} in{' '}
                    {partner?.person?.birth_place}
                  </>
                ),
              },
            ]}
          />

          <ConfirmSection
            card
            sectionTitle='Kontakt'
            step={getStepNumberByName('hasCoApplicant')}
            elements={[
              {
                title: 'Mobilnummer',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: (
                  <>
                    {partner?.person?.contact_details?.phone_numbers &&
                    partner?.person?.contact_details?.phone_numbers.length
                      ? partner?.person?.contact_details?.phone_numbers[0].value
                      : ''}
                  </>
                ),
              },
              {
                title: 'E-Mail Adresse',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: (
                  <>
                    {partner?.person?.contact_details?.emails &&
                    partner?.person?.contact_details?.emails.length
                      ? partner?.person?.contact_details?.emails[0].value
                      : ''}
                  </>
                ),
              },
            ]}
          />

          <ConfirmSection
            card
            sectionTitle='Bankverbindung'
            step={getStepNumberByName('hasCoApplicant')}
            elements={[
              {
                title: 'IBAN',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: `${
                  partner?.bank_account?.iban
                    ? ibanFormat(partner?.bank_account?.iban)
                    : ''
                }`,
              },
              {
                title: 'Bankinstitut',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: `${partner?.bank_account?.bank_name}`,
              },
            ]}
          />

          <ConfirmSection
            card
            sectionTitle='Arbeit'
            step={getStepNumberByName('hasCoApplicant')}
            elements={[
              {
                title: 'Beschäftigung',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: (
                  <>
                    {
                      getEmploymentByType(
                        partner?.living_details?.employment_type || '',
                      )?.label
                    }{' '}
                    | Seit {partner?.living_details?.employment_since} <br />
                  </>
                ),
              },
            ]}
          />

          <ConfirmSection
            card
            sectionTitle='Familie'
            step={getStepNumberByName('hasCoApplicant')}
            elements={[
              {
                title: 'Familienstand & Kinder',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: (
                  <>
                    {
                      getMaritalStatusByType(
                        partner?.living_details?.marital_status || '',
                      )?.label
                    }{' '}
                    |{' '}
                    {
                      getChildrenByNumber(
                        partner?.living_details?.number_of_children,
                      )?.value
                    }{' '}
                    unterhaltspflichtige Kinder <br />
                  </>
                ),
              },
            ]}
          />

          <ConfirmSection
            card
            sectionTitle='Wohnen'
            step={getStepNumberByName('hasCoApplicant')}
            elements={[
              {
                title: 'Wohnsituation',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: (
                  <>
                    {
                      getResidenceByType(
                        partner?.living_details?.residence_type,
                      )?.label
                    }
                    <br />
                    Seit {partner?.living_details?.residence_since}
                    <br />
                  </>
                ),
              },
            ]}
          />

          <ConfirmSection
            card
            sectionTitle='Einnahmen'
            step={getStepNumberByName('hasCoApplicant')}
            elements={[
              {
                title: 'Gesamte Einnahmen',
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: `${formatNumberWithThousandsSeparator(
                  `${
                    parseInt(
                      partner?.income_and_cost_details?.income?.units || '0',
                    ) +
                    parseInt(
                      partner?.income_and_cost_details?.additional_income
                        ?.units || '0',
                    )
                  }`,
                )} ${'€ / Monat'}`,
              },
            ]}
          />

          <ConfirmSection
            card
            sectionTitle='Ausgaben'
            step={getStepNumberByName('hasCoApplicant')}
            elements={[
              {
                title: `Gesamte Ausgaben`,
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: `${formatNumberWithThousandsSeparator(
                  `${
                    parseInt(
                      partner?.income_and_cost_details?.housing_cost?.units ||
                        '0',
                    ) +
                    parseInt(
                      partner?.income_and_cost_details?.additional_cost
                        ?.units || '0',
                    )
                  }`,
                )} ${'€ / Monat'}`,
              },
            ]}
          />

          <ConfirmSection
            card
            sectionTitle=''
            step={getStepNumberByName('hasCoApplicant')}
            elements={[
              {
                title: `Budget`,
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                value: `${formatNumberWithThousandsSeparator(
                  `${
                    parseInt(
                      partner?.income_and_cost_details?.income?.units || '0',
                    ) +
                    parseInt(
                      partner?.income_and_cost_details?.additional_income
                        ?.units || '0',
                    ) -
                    parseInt(
                      partner?.income_and_cost_details?.housing_cost?.units ||
                        '0',
                    ) +
                    parseInt(
                      partner?.income_and_cost_details?.additional_cost
                        ?.units || '0',
                    )
                  }`,
                )} ${'€'}`,
              },
            ]}
          />

          <ConfirmSection
            card
            sectionTitle='Dokumenten-Upload'
            step={getStepNumberByName('hasCoApplicant')}
            elements={coSignerDocuments?.map((document) => {
              const incomeDoc =
                document.document_type === 'PROOF_OF_INCOME'
                  ? document
                  : undefined;
              return {
                title: 'Einkommensnachweise',
                value: `${
                  incomeDoc ? ellipsisFileName(incomeDoc.file_name) : ''
                }`,
                titleClassName: 'small-text',
                valueClassName: 'small-text',
                onClick: () => openFile(incomeDoc),
              };
            })}
          />

          {partnerIdentityDocument() && (
            <ConfirmSection
              card
              sectionTitle='Ausweisdokument'
              step={getStepNumberByName('hasCoApplicant')}
              elements={[
                {
                  title: 'Meldebestätigung',
                  value: ellipsisFileName(
                    partnerIdentityDocument()?.file_name || '',
                  ),
                  condition: !!partnerIdentityDocument(),
                  onClick: () => openFile(partnerIdentityDocument()),
                },
              ]}
            />
          )}
        </div>
      )}

      <ButtonGroup
        type='back-next'
        className='back-next'
        buttonOneProps={{
          type: 'submit',
          loading,
          disabled: !canContinue,
          onClick: next,
          name: 'form-submit',
          dataTestId: 'next',
          children: 'Angaben bestätigen und fortfahren',
        }}
      />
    </Layout>
  );
};
export default DataSummary;
